import { useState } from "react";
import { SectionIcon } from "./SectionWidgets";

const HEALTH_DOT = { complete: "🟢", ready: "🟡", attention: "🟡", blocked: "🔴", na: "⚪" };

export default function SectionSidebar({
  isMobile,
  sections,
  activeSection,
  getNextRequiredAction,
  getSectionHealth,
  openActions,
  selectSection,
}) {
  const [pickerOpen, setPickerOpen] = useState(false);

  if (isMobile) {
    return (
      <>
        <button
          onClick={() => setPickerOpen(true)}
          style={{
            position: "absolute", top: 0, left: 0, right: 0, zIndex: 10,
            display: "flex", alignItems: "center", gap: 8,
            padding: "10px 12px 10px 14px", background: "#fafbfc",
            border: "none", borderBottom: "1px solid var(--cs-border)",
            boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
            cursor: "pointer", textAlign: "left", width: "100%",
          }}
        >
          {activeSection && <SectionIcon section={activeSection} />}
          <span style={{ fontSize: 11, color: "var(--text3)", flexShrink: 0 }}>
            §{activeSection?.SectionNumber}
          </span>
          <span style={{ fontSize: 13, fontWeight: 600, color: "var(--text)", flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {activeSection?.SectionName}
          </span>
          {openActions.length > 0 && (
            <span style={{ fontSize: 10, color: "#dc2626", fontWeight: 700, flexShrink: 0 }}>
              {openActions.length} open
            </span>
          )}
          <span style={{
            display: "flex", alignItems: "center", gap: 4, flexShrink: 0,
            padding: "5px 10px", borderRadius: 20,
            background: "var(--cs-blue-lt)", color: "var(--cs-blue)",
            fontSize: 11, fontWeight: 700,
          }}>
            Sections
            <span style={{ fontSize: 9 }}>▾</span>
          </span>
        </button>

        {pickerOpen && (
          <>
            <div
              onClick={() => setPickerOpen(false)}
              style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.35)", zIndex: 150 }}
            />
            <div style={{
              position: "fixed", left: 0, right: 0, bottom: 0, zIndex: 151,
              background: "#fafbfc", borderTopLeftRadius: 14, borderTopRightRadius: 14,
              maxHeight: "75vh", overflowY: "auto",
              boxShadow: "0 -8px 30px rgba(0,0,0,0.25)",
            }}>
              <div style={{
                padding: "12px 14px", fontSize: 11, fontWeight: 700, color: "var(--text3)",
                textTransform: "uppercase", letterSpacing: "0.06em",
                borderBottom: "1px solid var(--cs-border)", position: "sticky", top: 0, background: "#fafbfc",
                display: "flex", justifyContent: "space-between", alignItems: "center",
              }}>
                Sections
                <button onClick={() => setPickerOpen(false)} style={{ background: "none", border: "none", fontSize: 18, color: "var(--text3)", cursor: "pointer", lineHeight: 1 }}>✕</button>
              </div>
              {sections.map(sec => {
                const isActive = activeSection?.SectionID === sec.SectionID;
                const health = getSectionHealth(sec);
                return (
                  <div
                    key={sec.SectionID}
                    onClick={() => { selectSection(sec); setPickerOpen(false); }}
                    style={{
                      display: "flex", alignItems: "center", gap: 8,
                      padding: "12px 14px", cursor: "pointer",
                      background: isActive ? "#eff6ff" : "transparent",
                      borderLeft: isActive ? "3px solid var(--cs-blue)" : "3px solid transparent",
                    }}
                  >
                    <span style={{ fontSize: 12, width: 16, flexShrink: 0, textAlign: "center" }}>
                      {HEALTH_DOT[health.level] || ""}
                    </span>
                    <span style={{ fontSize: 11, color: "var(--text3)", width: 16, flexShrink: 0, textAlign: "right" }}>
                      {sec.SectionNumber}
                    </span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{
                        fontSize: 13, fontWeight: isActive ? 600 : 400,
                        color: !sec.IsApplicable ? "var(--text3)" : "var(--text)",
                        textDecoration: !sec.IsApplicable ? "line-through" : "none",
                        overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                      }}>
                        {sec.SectionName}
                      </div>
                      {health.level === "attention" ? (
                        <div style={{ fontSize: 10, color: "#b45309", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", marginTop: 1 }}>
                          {health.reason}
                        </div>
                      ) : sec.FileName && (
                        <div style={{ fontSize: 10, color: "var(--text3)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", marginTop: 1 }}>
                          {sec.FileName}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
              <div style={{ padding: "10px 14px", marginTop: 4, borderTop: "1px solid var(--cs-border)" }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: "var(--text3)", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 4 }}>
                  Action Items
                </div>
                <div style={{ fontSize: 12, color: openActions.length > 0 ? "#dc2626" : "#10b981", fontWeight: 600 }}>
                  {openActions.length > 0 ? `${openActions.length} open` : "All closed"}
                </div>
              </div>
            </div>
          </>
        )}
      </>
    );
  }

  // Desktop sidebar
  const next = getNextRequiredAction();
  const currentNum = next?.targetSection || null;
  const isDone = s => {
    if (!s.IsApplicable) return true;
    // A completed section can still fail a data check (§7 estimate ≠ release).
    if (s.IsComplete) return getSectionHealth(s).level !== "attention";
    // Cover Sheet moves to Completed once all required fields are filled (no manual click needed)
    if (s.SectionNumber === 1) {
      const h = getSectionHealth(s);
      return h.level === "ready" || h.level === "complete";
    }
    return false;
  };
  const currentSec = currentNum ? sections.find(s => s.SectionNumber === currentNum) : null;
  const upcoming  = sections.filter(s => s !== currentSec && !isDone(s)).sort((a, b) => a.SectionNumber - b.SectionNumber);
  const completed = sections.filter(s => s !== currentSec && isDone(s)).sort((a, b) => a.SectionNumber - b.SectionNumber);

  function Row(sec) {
    const isActive = activeSection?.SectionID === sec.SectionID;
    const health = getSectionHealth(sec);
    return (
      <div
        key={sec.SectionID}
        onClick={() => selectSection(sec)}
        title={health.reason || health.label}
        style={{
          display: "flex", alignItems: "center", gap: 8,
          padding: "8px 14px", cursor: "pointer",
          background: isActive ? "#eff6ff" : "transparent",
          borderLeft: isActive ? "3px solid var(--cs-blue)" : "3px solid transparent",
          transition: "all 0.1s",
        }}
        onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = "#f3f4f6"; }}
        onMouseLeave={e => { if (!isActive) e.currentTarget.style.background = "transparent"; }}
      >
        <span style={{ fontSize: 12, width: 16, flexShrink: 0, textAlign: "center" }}>
          {HEALTH_DOT[health.level] || ""}
        </span>
        <span style={{ fontSize: 11, color: "var(--text3)", width: 16, flexShrink: 0, textAlign: "right" }}>
          {sec.SectionNumber}
        </span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            fontSize: 12, fontWeight: isActive ? 600 : 400,
            color: !sec.IsApplicable ? "var(--text3)" : "var(--text)",
            textDecoration: !sec.IsApplicable ? "line-through" : "none",
            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
          }}>
            {sec.SectionName}
          </div>
          {health.level === "attention" ? (
            <div style={{ fontSize: 10, color: "#b45309", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", marginTop: 1 }}>
              {health.reason}
            </div>
          ) : health.level === "ready" && health.reason ? (
            <div style={{ fontSize: 10, color: "#92400e", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", marginTop: 1 }}>
              {health.reason}
            </div>
          ) : sec.FileName && (
            <div style={{ fontSize: 10, color: "var(--text3)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", marginTop: 1 }}>
              {sec.FileName}
            </div>
          )}
        </div>
      </div>
    );
  }

  function GroupLabel(text) {
    return (
      <div style={{ padding: "12px 14px 6px", fontSize: 11, fontWeight: 700, color: "var(--text3)", textTransform: "uppercase", letterSpacing: "0.06em" }}>
        {text}
      </div>
    );
  }

  return (
    <div style={{
      width: 260, flexShrink: 0, borderRight: "1px solid var(--cs-border)",
      overflowY: "auto", background: "#fafbfc",
    }}>
      {completed.length > 0 && (
        <>
          {GroupLabel("Completed")}
          {completed.map(Row)}
        </>
      )}
      {currentSec && (
        <>
          {GroupLabel("Current")}
          {Row(currentSec)}
        </>
      )}
      {upcoming.length > 0 && (
        <>
          {GroupLabel("Upcoming")}
          {upcoming.map(Row)}
        </>
      )}

      <div style={{ padding: "10px 14px", marginTop: 4, borderTop: "1px solid var(--cs-border)" }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: "var(--text3)", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 4 }}>
          Action Items
        </div>
        <div style={{ fontSize: 12, color: openActions.length > 0 ? "#dc2626" : "#10b981", fontWeight: 600 }}>
          {openActions.length > 0 ? `${openActions.length} open` : "All closed"}
        </div>
      </div>
    </div>
  );
}
